import { useCallback, useEffect, useRef, useState } from 'react'
import { useBrowserNotificationSettings } from '@/hooks/queries/useBrowserNotificationSettings'
import { useNotificationDelivery } from '@/hooks/queries/useNotificationDelivery'
import { useNotificationReadSession } from '@/hooks/queries/useNotificationReadSession'
import { getNotificationRoute } from '@/lib/notifications/notification-route'
import { isOwnedNotificationDeliveryCurrent } from '@/lib/notifications/owned-notification-delivery'
import { isTauri } from '@/lib/utils/tauri'
import { browserPreferenceInput } from '@/modules/notifications/browser-preferences'
import { browserNotificationService } from '@/services/browserNotificationService'
import { useBennyStore } from '@/store/benny.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'
import { toast } from 'sonner'

import {
  AppPreferences,
  BrowserNotificationPreferences,
  DesktopNotificationSettings,
  NotificationType,
} from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

type NotificationHandler = (notification: any) => void

// Ensure we only register one window listener to avoid duplicate native notifications.
const notificationHandlers = new Set<NotificationHandler>()
let notificationListenerAttached = false

const getActiveNotificationHandler = (): NotificationHandler | null => {
  let handler: NotificationHandler | null = null
  for (const entry of notificationHandlers) {
    handler = entry
  }
  return handler
}

const sharedNotificationListener = (event: Event) => {
  const handler = getActiveNotificationHandler()
  if (!handler) return
  const customEvent = event as CustomEvent
  handler(customEvent.detail)
}

export const useBrowserNotifications = () => {
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const settings = useBrowserNotificationSettings()
  const { identity, preferences } = settings
  const assertPreferenceSession = settings.assertCurrent
  const [action, setAction] = useState<{
    identity: string
    pending: number
    failed: boolean
  }>({ identity, pending: 0, failed: false })
  const permissionFlight = useRef<string | null>(null)
  useNotificationDelivery()
  const notificationSession = useNotificationReadSession()
  const badgeLeaseCurrent = useCallback(() => {
    try {
      notificationSession.assertCurrent()
      return true
    } catch {
      return false
    }
  }, [notificationSession])
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )

  const assertActionSession = () => {
    assertPreferenceSession()
    if (!mounted.current) throw new SessionChangedError()
  }
  const run = async <T>(operation: () => Promise<T>): Promise<T> => {
    assertActionSession()
    setAction(current => ({
      identity,
      pending: (current.identity === identity ? current.pending : 0) + 1,
      failed: false,
    }))
    try {
      return await operation()
    } catch (error) {
      try {
        assertActionSession()
        setAction(current =>
          current.identity === identity
            ? { ...current, failed: true }
            : current,
        )
        toast.error(
          'The notification setting could not be confirmed. Refresh before trying again.',
        )
      } catch {
        /* No feedback for a retired account. */
      }
      if (error instanceof SessionChangedError) throw error
      throw new Error('Notification settings could not be confirmed')
    } finally {
      if (mounted.current)
        setAction(current =>
          current.identity === identity
            ? { ...current, pending: Math.max(0, current.pending - 1) }
            : current,
        )
    }
  }
  const update = settings.update
  const saveBrowserNotificationPreferences = async (
    value: BrowserNotificationPreferences,
  ) => {
    const captured = browserPreferenceInput.body(structuredClone(value))
    return run(() => update(current => ({ ...current, ...captured })))
  }
  const withPermission = async <T>(choice: () => Promise<T>) => {
    assertActionSession()
    settings.assertWritable()
    if (permissionFlight.current === identity)
      throw new Error('Permission choice is already in progress')
    permissionFlight.current = identity
    try {
      return await choice()
    } finally {
      if (permissionFlight.current === identity) permissionFlight.current = null
    }
  }
  const readPermission = () =>
    browserNotificationService.requestPermission(() => {
      try {
        assertActionSession()
        return true
      } catch {
        return false
      }
    })
  const requestPermission = () =>
    run(() =>
      withPermission(async () => {
        const permission = await readPermission()
        assertActionSession()
        await update(current => ({
          ...current,
          permissionRequested: true,
          permissionStatus: permission,
          enabled: permission === 'granted' && current.enabled,
        }))
        return permission
      }),
    )
  const enableBrowserNotifications = () =>
    run(() =>
      withPermission(async () => {
        const permission = await readPermission()
        assertActionSession()
        await update(current => ({
          ...current,
          enabled: permission === 'granted',
          userDisabled: false,
          permissionRequested: true,
          permissionStatus: permission,
        }))
        return permission === 'granted'
      }),
    )
  const disableBrowserNotifications = () =>
    run(() =>
      update(current => ({ ...current, enabled: false, userDisabled: true })),
    )
  const updateNotificationTypes = async (
    value: Partial<NonNullable<BrowserNotificationPreferences['enabledTypes']>>,
  ) => {
    const captured = browserPreferenceInput.categories(structuredClone(value))
    return run(() =>
      update(current => ({
        ...current,
        enabledTypes: { ...current.enabledTypes, ...captured },
      })),
    )
  }
  const updateDisplaySettings = async (
    value: Pick<
      BrowserNotificationPreferences,
      | 'showWhenTabActive'
      | 'showWhenTabInactive'
      | 'playSound'
      | 'autoDismissMs'
    >,
  ) => {
    const captured = browserPreferenceInput.display(structuredClone(value))
    return run(() => update(current => ({ ...current, ...captured })))
  }
  const updateDesktopSettings = async (
    value: Partial<DesktopNotificationSettings>,
  ) => {
    const captured = browserPreferenceInput.desktop(structuredClone(value))
    return run(() =>
      update(current => ({
        ...current,
        desktopSettings: { ...current.desktopSettings, ...captured },
      })),
    )
  }
  const refreshBrowserNotificationPreferences = () => run(settings.refresh)

  const testFlight = useRef<string | null>(null)
  const [testAction, setTestAction] = useState<{
    identity: string
    pending: boolean
  }>({ identity: '', pending: false })
  const sendTestNotification = async () => {
    assertActionSession()
    if (testFlight.current === identity)
      throw new Error('Device test already in progress')
    testFlight.current = identity
    setTestAction({ identity, pending: true })
    const current = () => {
      try {
        assertActionSession()
        return true
      } catch {
        return false
      }
    }
    try {
      const result = await withPermission(() =>
        browserNotificationService.sendTestNotification(current),
      )
      assertActionSession()
      if (result === 'submitted')
        toast.success('Test submitted. Check your notification center.')
      else if (result === 'denied')
        toast.error('Notifications are blocked. Check your device permissions.')
      else if (result === 'unavailable')
        toast.error('Device notifications are unavailable here.')
      return result
    } catch (error) {
      if (current()) toast.error('The device test could not be confirmed')
      if (error instanceof SessionChangedError) throw error
      throw new Error('The device test could not be confirmed')
    } finally {
      if (testFlight.current === identity) testFlight.current = null
      if (current()) setTestAction({ identity, pending: false })
    }
  }

  // Update badge count (Tauri app only)
  const updateBadgeCount = useCallback(
    async (count: number): Promise<void> => {
      if (!isTauri()) return

      const desktopSettings = preferences.desktopSettings
      if (desktopSettings?.showBadgeCount !== false) {
        await browserNotificationService.setBadgeCount(count, badgeLeaseCurrent)
      }
    },
    [preferences.desktopSettings, badgeLeaseCurrent],
  )

  // Clear badge count (Tauri app only)
  const clearBadgeCount = useCallback(async (): Promise<void> => {
    if (!isTauri()) return
    await browserNotificationService.clearBadgeCount(badgeLeaseCurrent)
  }, [badgeLeaseCurrent])

  // Handle incoming notifications and show browser notifications
  const handleIncomingNotification = useCallback(
    (notification: any) => {
      if (!isOwnedNotificationDeliveryCurrent(notification)) return
      const isCurrent = () =>
        mounted.current && isOwnedNotificationDeliveryCurrent(notification)
      const currentPrefs = preferences

      if (!currentPrefs.enabled || !browserNotificationService.isSupported()) {
        return
      }

      // When the floating Benny companion is delivering notifications
      // (speech bubbles), skip the native system notification so the user
      // isn't notified twice. BennyCompanionController forwards the same
      // notification to the companion window.
      if (isTauri() && useBennyStore.getState().companionActive) {
        const appPrefs = getUserAppPreference(
          useUserPreferencesStore.getState().preferences,
        )?.preferences as AppPreferences | undefined
        if (appPrefs?.bennyNotificationsEnabled === true) {
          return
        }
      }

      const notificationData = (notification?.data ?? {}) as Record<
        string,
        unknown
      >
      const organizationSlug = currentOrganization?.slug
      const taskIdentifierRaw =
        notificationData.taskNumber ??
        notificationData.taskId ??
        notification?.entityId
      const taskIdentifier = taskIdentifierRaw
        ? String(taskIdentifierRaw)
        : undefined
      const projectIdRaw =
        notificationData.projectId ?? notificationData.moduleId
      const projectId = projectIdRaw ? String(projectIdRaw) : undefined
      const threadIdRaw =
        notificationData.threadId ??
        (notification?.entityType === 'THREAD'
          ? notification?.entityId
          : undefined)
      const threadId = threadIdRaw ? String(threadIdRaw) : undefined
      const noteIdRaw =
        notificationData.noteId ??
        (notification?.entityType === 'NOTE'
          ? notification?.entityId
          : undefined)
      const noteId = noteIdRaw ? String(noteIdRaw) : undefined
      const threadType =
        notificationData.threadType === 'DIRECT_MESSAGE' ||
        notificationData.threadType === 'DISCUSSION'
          ? (notificationData.threadType as 'DIRECT_MESSAGE' | 'DISCUSSION')
          : notificationData.isDirectMessage
            ? 'DIRECT_MESSAGE'
            : undefined
      const bodyText =
        notification.message ||
        notification.title ||
        'You have a new notification'
      const notificationRoute = getNotificationRoute(
        organizationSlug,
        notification,
      )

      const buildEntityUrl = () => {
        if (notificationRoute) return notificationRoute
        if (!organizationSlug) return undefined
        switch (notification?.entityType) {
          case 'TASK':
            return taskIdentifier
              ? `/${organizationSlug}/tasks/${taskIdentifier}`
              : undefined
          case 'PROJECT':
            return notification?.entityId
              ? `/${organizationSlug}/projects/${notification.entityId}`
              : undefined
          case 'THREAD': {
            if (!threadId) return undefined
            return `/${organizationSlug}/threads?tab=discussions&thread=${threadId}`
          }
          case 'NOTE':
            return noteId ? `/${organizationSlug}/notes/${noteId}` : undefined
          default:
            return undefined
        }
      }
      const entityUrl = buildEntityUrl()

      // Determine notification type and show appropriate browser notification
      switch (notification.type) {
        case NotificationType.TASK_ASSIGNED:
          browserNotificationService.showAssignmentNotification(
            'Task Assigned',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
            isCurrent,
          )
          break

        case NotificationType.TASK_STATUS_CHANGED:
          browserNotificationService.showTaskNotification(
            'Task Status Changed',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
            isCurrent,
          )
          break

        case NotificationType.TASK_DUE_SOON:
          browserNotificationService.showDeadlineNotification(
            'Task Due Soon',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
            isCurrent,
          )
          break

        case NotificationType.TASK_OVERDUE:
          browserNotificationService.showDeadlineNotification(
            'Task Overdue',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
            isCurrent,
          )
          break

        case NotificationType.TASK_COMMENT_ADDED:
        case NotificationType.COMMENT_REPLY:
          if (threadId && notification?.entityType === 'THREAD') {
            browserNotificationService.showThreadMessageNotification(
              notification.title || 'New comment',
              bodyText,
              threadId,
              organizationSlug,
              threadType,
              isCurrent,
            )
          } else {
            browserNotificationService.showCommentNotification(
              'New Comment',
              bodyText,
              taskIdentifier,
              threadId,
              organizationSlug,
              entityUrl,
              isCurrent,
            )
          }
          break

        case NotificationType.TASK_MENTION:
        case NotificationType.COMMENT_MENTION:
        case NotificationType.NOTE_MENTION:
          if (threadId && notification?.entityType === 'THREAD') {
            browserNotificationService.showThreadMessageNotification(
              notification.title || 'Mentioned in a thread',
              bodyText,
              threadId,
              organizationSlug,
              threadType,
              isCurrent,
            )
          } else {
            browserNotificationService.showMentionNotification(
              'You were mentioned',
              bodyText,
              taskIdentifier,
              threadId,
              organizationSlug,
              entityUrl,
              isCurrent,
            )
          }
          break

        default:
          browserNotificationService.showSystemNotification(
            notification.title || 'New Notification',
            bodyText,
            entityUrl,
            isCurrent,
          )
          break
      }
    },
    [currentOrganization?.slug, preferences],
  )

  // Update service preferences when preferences change (local only, no API call)
  useEffect(() => {
    browserNotificationService.updatePreferences(preferences)
  }, [preferences])

  // Mounting only installs local readers/listeners. Permission prompts and
  // preference writes belong exclusively to an explicit user action.

  // Listen for notification created events from the store
  useEffect(() => {
    notificationHandlers.add(handleIncomingNotification)
    if (!notificationListenerAttached) {
      window.addEventListener(
        'scrambl:notification-created',
        sharedNotificationListener as EventListener,
      )
      notificationListenerAttached = true
    }

    return () => {
      notificationHandlers.delete(handleIncomingNotification)
      if (notificationHandlers.size === 0 && notificationListenerAttached) {
        window.removeEventListener(
          'scrambl:notification-created',
          sharedNotificationListener as EventListener,
        )
        notificationListenerAttached = false
      }
    }
  }, [handleIncomingNotification])

  return {
    // State
    preferences,
    isSaving:
      settings.isSaving || (action.identity === identity && action.pending > 0),
    canEdit: settings.canEdit,
    canRefresh: settings.canRefresh,
    unavailable: settings.unavailable,
    saveFailed:
      settings.saveFailed || (action.identity === identity && action.failed),
    refreshBrowserNotificationPreferences,
    isSupported: browserNotificationService.isSupported(),
    permissionStatus: browserNotificationService.getPermissionStatus(),
    isDesktopApp: isTauri(),

    // Actions
    requestPermission,
    sendTestNotification,
    isTesting: testAction.identity === identity && testAction.pending,
    enableBrowserNotifications,
    disableBrowserNotifications,
    updateNotificationTypes,
    updateDisplaySettings,
    updateDesktopSettings,
    saveBrowserNotificationPreferences,

    // Badge count (desktop only)
    updateBadgeCount,
    clearBadgeCount,

    // Handlers
    handleIncomingNotification,
  }
}
